<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";

import { useSidebarBackground } from "@/common/composables/useSidebarBackground";
import { WHITE_MWS_LOGO_URL } from "@/common/utils/constants";

import NavigationContent from "./NavigationContent.vue";
import BaseIcon from "../icons/BaseIcon.vue";

defineProps<{
  isSidebarOpen: boolean;
}>();

defineEmits<{
  (e: "close-sidebar-click"): void;
}>();

const { sidebarBackgroundClass } = useSidebarBackground();
</script>

<template>
  <TransitionRoot
    as="template"
    :show="isSidebarOpen"
  >
    <Dialog
      as="div"
      class="relative z-40 md:hidden"
      @close="$emit('close-sidebar-click')"
    >
      <TransitionChild
        as="template"
        enter="transition-opacity ease-linear duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-600/75" />
      </TransitionChild>

      <div class="fixed inset-0 z-40 flex">
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <DialogPanel
            class="relative flex w-full max-w-xs flex-1 flex-col pt-5 pb-4"
            :class="sidebarBackgroundClass"
          >
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  class="ml-1 flex size-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  @click="$emit('close-sidebar-click')"
                >
                  <span class="sr-only">Close sidebar</span>
                  <BaseIcon
                    name="heroicons:x-mark"
                    class="size-6 text-white"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </TransitionChild>
            <div class="flex shrink-0 items-center px-4">
              <img
                class="mx-auto h-8 w-auto"
                :src="WHITE_MWS_LOGO_URL"
                alt="MWS"
              />
            </div>
            <div class="mt-5 h-0 flex-1 overflow-y-auto">
              <nav class="space-y-1 px-2">
                <NavigationContent
                  @close-sidebar-click="$emit('close-sidebar-click')"
                />
              </nav>
            </div>
          </DialogPanel>
        </TransitionChild>
        <div
          class="w-14 shrink-0"
          aria-hidden="true"
        >
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
